import React, { useEffect, useState, useContext } from "react";
import QRCode from "qrcode.react";
import toastError from "../../errors/toastError";
import { makeStyles } from "@material-ui/core/styles";
import { Dialog, DialogContent, Paper, Typography } from "@material-ui/core";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";

import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  content: {
    backgroundColor: theme.mode === "light" ? "#fff" : "#172838",
    color: theme.mode === "light" ? "#1d2a38" : "#f4f8fc",
    padding: theme.spacing(2),
  },
  instruction: {
    color: theme.mode === "light" ? "#174d79" : "#d9ecff",
    fontWeight: 600,
  },
  waiting: {
    color: theme.mode === "light" ? "#1d2a38" : "#f4f8fc",
    fontWeight: 600,
  },
  timestamp: {
    color: theme.mode === "light" ? "#5d6c7a" : "#c4d1dc",
  },
}))

const QrcodeModal = ({ open, onClose, whatsAppId }) => {
  const classes = useStyles();
  const [qrCode, setQrCode] = useState("");
  const [lastUpdatedAt, setLastUpdatedAt] = useState("");
  const [sessionStatus, setSessionStatus] = useState("");
  const { user, socket } = useContext(AuthContext);

  useEffect(() => {
    let isMounted = true;

    const fetchSession = async () => {
      if (!whatsAppId || !open) return;

      try {
        const { data } = await api.get(`/whatsapp/${whatsAppId}?session=0&_ts=${Date.now()}`);

        if (!isMounted) return;

        setQrCode(data?.qrcode || "");
        setLastUpdatedAt(data?.updatedAt || "");
        setSessionStatus(data?.status || "");
      } catch (err) {
        if (isMounted) {
          toastError(err);
        }
      }
    };

    fetchSession();

    const interval = setInterval(fetchSession, 2000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [open, whatsAppId]);

  useEffect(() => {
    if (!whatsAppId || !open || !user?.companyId || !socket?.on || !socket?.off) {
      return undefined;
    }

    const companyId = user.companyId;

    const onWhatsappData = (data) => {
      if (data.action === "update" && data.session.id === whatsAppId) {
        setQrCode(data.session.qrcode || "");
        setLastUpdatedAt(data.session.updatedAt || "");
        setSessionStatus(data.session.status || "");
      }

      if (
        data.action === "update" &&
        data.session.id === whatsAppId &&
        data.session.status === "CONNECTED"
      ) {
        onClose();
      }
    };

    socket.on(`company-${companyId}-whatsappSession`, onWhatsappData);

    return () => {
      socket.off(`company-${companyId}-whatsappSession`, onWhatsappData);
    };
  }, [open, whatsAppId, onClose, socket, user?.companyId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" scroll="paper">
      <DialogContent className={classes.content}>
        <Paper elevation={0} className={classes.content}>
          <Typography className={classes.instruction} gutterBottom>
            {i18n.t("qrCode.message")}
          </Typography>
          <div className={classes.root}>
            {qrCode ? (
              <QRCode
                key={qrCode}
                value={qrCode}
                size={300}
                style={{ backgroundColor: "white", padding: '5px' }}
              />
            ) : sessionStatus === "DISCONNECTED" ? (
              <Typography color="error">
                WhatsApp rechazo temporalmente la conexion. Espere un momento y pruebe de nuevo.
              </Typography>
            ) : (
              <span className={classes.waiting}>{i18n.t("qrcodeModal.waiting")}</span>
            )}
          </div>
          {lastUpdatedAt ? (
            <Typography variant="caption" className={classes.timestamp}>
              Actualizado: {new Date(lastUpdatedAt).toLocaleTimeString("es-CO")}
            </Typography>
          ) : null}
        </Paper>
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(QrcodeModal);
